import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { RequestsStatsEnum } from './models/requests/requests-stats.enum';
import { HttpClientHelperService } from './http-client-helper.service';
import { IntegrationsIdenntifiersResponse, IsAutopilotValidResponse, PreviewCopyDataResponse, SearchTriggerResponse, SubmitTicketForm, TaskSearchResponse, TicketMetadata, UpdateTicketDetails } from './models/ticket/ticket.interface';
import { RequestsListPayload, RequestsPaginationPayload, RequestsSearchPayload, ViewsCountersPayload } from '../requests/models/requests.interface';
import { AutopilotAssignedResponse, CountryCodesResponse, DataSubjectLabelsResponse, ExportAuditLogResponse, RequestItemDeep, RequestsResponse, RequestsStatsResponse, ViewsCountersResponse } from './models/requests/requests.interface';
import { RequestFormResponse } from '../requests/request-form/models/interfaces';
import { SortDirectionEnum } from '../shared/sort-direction.enum';
import { RequestHistoryResponse } from './models/requests/request-history';
import { INTEGRATIONS_GATEWAY } from './api-gateway.constants';
import { ListV2Response } from './models/requests/requests-v2.interface';

@Injectable({
	providedIn: 'root'
})
export class ApiClientTicketService {

	private readonly controller = 'tickets';
	private readonly PAGINATION_MAX_SIZE = 1000;
	private readonly PAGINATION_DEFAULT_SIZE = 100;
	private readonly PAGINATION_V2_DEFAULT_SIZE = 25;
	private readonly PAGINATION_V2_DEFAULT_PAGE = 0;

	constructor(private httpClientHelper: HttpClientHelperService) { }

	list(payload?: RequestsListPayload): Observable<RequestsResponse> {
		const qParams = this.buildFilterQueryParams(payload, this.getPaginationQueryParams(payload));
		return this.httpClientHelper.invokeGetAuth<RequestsResponse>(`${this.controller}/list`, qParams);
	}

	search(payload: RequestsSearchPayload): Observable<RequestsResponse> {
		let qParams = this.getPaginationQueryParams(payload);
	
		qParams = qParams.set('term', payload.term);
		
		if (payload?.direction) {
			qParams = qParams.set('direction', SortDirectionEnum[payload.direction].toLowerCase());
		}

		return this.httpClientHelper.invokeGetAuth<RequestsResponse>(`${this.controller}/search`, qParams);
	}

	getViewsCounters(viewsCountersPayload: ViewsCountersPayload): Observable<ViewsCountersResponse> {
		return this.httpClientHelper.invokePostAuth<ViewsCountersResponse>(`${this.controller}/counts`, viewsCountersPayload);
	}

	getTicketInfo(id: string): Observable<RequestItemDeep> {
		return this.httpClientHelper.invokeGetAuth<RequestItemDeep>(`${this.controller}/${id}/info`);
	}

	getStats(stats: RequestsStatsEnum): Observable<RequestsStatsResponse> {
		return this.httpClientHelper.invokeGetAuth<RequestsStatsResponse>(`${this.controller}/stats/${stats}`); 
	}

	getCountryCodes(): Observable<CountryCodesResponse> {
		return this.httpClientHelper.invokeGetAuth<CountryCodesResponse>(`${this.controller}/country-codes`);
	}

	getDataSubjectLabels(): Observable<DataSubjectLabelsResponse> {
		return this.httpClientHelper.invokeGetAuth<DataSubjectLabelsResponse>(`${this.controller}/data-subject-labels`);
	}

	updateMetadata(data: TicketMetadata): Observable<void> {
		return this.httpClientHelper.invokePostAuth<void>(`${this.controller}/updateMetadata`, data);
	}

	markTicketsAsUnread(ticketIds: string[]): Observable<void> {
		return this.httpClientHelper.invokePostAuth<void>(`${this.controller}/MarkUnread`, ticketIds);
	}

	manualIntegrationSearch(ticketId: string, integrationId: string): Observable<SearchTriggerResponse> {
		return this.httpClientHelper.invokePostAuth<SearchTriggerResponse>(`${INTEGRATIONS_GATEWAY}/${this.controller}/${ticketId}/integrations/v3/${integrationId}/actions/search`);
	}

	searchAll(ticketId: string, ids: string[]): Observable<TaskSearchResponse[]> {
		return this.httpClientHelper.invokePostAuth<TaskSearchResponse[]>(`${INTEGRATIONS_GATEWAY}/${this.controller}/${ticketId}/integrations/v3/actions/search`, { tasks: ids });
	}

	getIdentifiers(ticketId: string): Observable<IntegrationsIdenntifiersResponse> {
		return this.httpClientHelper.invokeGet<IntegrationsIdenntifiersResponse>(`${INTEGRATIONS_GATEWAY}/${this.controller}/${ticketId}/integrations/v3/identifiers`);
	}

	updateTicketDetails(ticketId: string, details: UpdateTicketDetails): Observable<void> {
		return this.httpClientHelper.invokePatchAuth<void>(`${this.controller}/${ticketId}`, details);
	}

	assignAutopilot(ticketIds: string[]): Observable<AutopilotAssignedResponse> {
		return this.httpClientHelper.invokePostAuth<AutopilotAssignedResponse>(`${this.controller}/v1/assign-auto-pilot/`, {ticketIds});
	}

	exportAuditLog(): Observable<ExportAuditLogResponse> {
		return this.httpClientHelper.invokePostAuth<ExportAuditLogResponse>(`${this.controller}/AuditExport`);
	}

	private getPaginationQueryParams(payload?: RequestsPaginationPayload): HttpParams {
		let qParams = new HttpParams();
		if (payload?.before) {
			qParams = qParams.set('before', payload.before);
		}
		if (payload?.after) {
			qParams = qParams.set('after', payload.after);
		}
		if (payload?.size) {
			qParams = qParams.set('size', payload.size > this.PAGINATION_MAX_SIZE ? this.PAGINATION_MAX_SIZE : payload.size);
		}
		else {
			qParams = qParams.set('size', this.PAGINATION_DEFAULT_SIZE);
		}
		return qParams;
	}

	deleteTickets(ticketIds: string[]): Observable<void> {
		let qParams = new HttpParams();
		qParams = qParams.set('ids', ticketIds.toString());
		return this.httpClientHelper.invokeDeleteAuth<void>(`${this.controller}/tickets`, null, qParams);
	}

	continueAfterEditTicket(ticketForm: SubmitTicketForm, ticketId: string): Observable<void> {
		return this.httpClientHelper.invokePostAuth<void>(`${this.controller}/${ticketId}`, ticketForm);
	}
	
	createTicket(ticketForm: SubmitTicketForm): Observable<RequestFormResponse> {
		return this.httpClientHelper.invokePostAuth<RequestFormResponse>(`${this.controller}/create`, ticketForm);
	}

	getTicketLog(ticketId: string): Observable<RequestHistoryResponse> {
		return this.httpClientHelper.invokeGetAuth<RequestHistoryResponse>(`${this.controller}/${ticketId}/log`);
	}

	updateGroupAsignee(ticketId: string, groupId: string): Observable<void> {
		return this.httpClientHelper.invokePost<void>(`${this.controller}/${ticketId}/group-assignment`, {groupId});
	}

	deleteGroupAsignee(ticketId: string): Observable<void> {
		return this.httpClientHelper.invokeDeleteAuth<void>(`${this.controller}/${ticketId}/group-assignment`);
	}

	editCustomField(ticketId: string, key: string, value: string): Observable<void> {
		return this.httpClientHelper.invokePatchAuth<void>(`${this.controller}/${ticketId}/custom-fields`, { key, value });
	}

	deleteCustomField(ticketId: string, key: string): Observable<void> {
		return this.httpClientHelper.invokeDeleteAuth<void>(`${this.controller}/${ticketId}/custom-fields`, { key });
	}

	stopPendingAutopilot(ticketId: string): Observable<void> {
		return this.httpClientHelper.invokePostAuth<void>(`${this.controller}/v1/${ticketId}/auto-pilot`);
	}

	updateRequireAttentionStatus(ticketId: string, requireAttention: boolean): Observable<void> {
		return this.httpClientHelper.invokePostAuth<void>(`${this.controller}/v1/${ticketId}/require-attention`, { markedAsRequireAttention: requireAttention });
	}
	
	getCopyReportData(ticketId: string): Observable<PreviewCopyDataResponse> {
		return this.httpClientHelper.invokeGetAuth<PreviewCopyDataResponse>(`${this.controller}/${ticketId}/internal-copy-data`);
	}

	getFileMetadata(url: any): Observable<any> {
		return this.httpClientHelper.invokeGet<any>(url, null, true);
	}

	validateAutopilotStatus(ticketId: string): Observable<IsAutopilotValidResponse> {
		return this.httpClientHelper.invokeGetAuth<IsAutopilotValidResponse>(`${this.controller}/v1/${ticketId}/is-valid-auto-pilot`);
	}

	listV2(payload?: RequestsListPayload): Observable<ListV2Response> {
		const qParams = this.buildFilterQueryParams(payload, this.getV2PaginationQueryParams(payload));
		return this.httpClientHelper.invokeGetAuth<ListV2Response>(`${this.controller}/get-list`, qParams);
	}

	private buildFilterQueryParams(payload: RequestsListPayload | undefined, qParams: HttpParams): HttpParams {
		if (payload?.source?.length) {
			qParams = qParams.set('source', payload.source.toString());
		}
		if (payload?.state?.length) {
			qParams = qParams.set('state', payload.state.toString());
		}
		if (payload?.country?.length) {
			qParams = qParams.set('country', payload.country.toString());
		}
		if (payload?.domain?.length) {
			qParams = qParams.set('domain', payload.domain.toString());
		}
		if (payload?.brandIds?.length) {
			qParams = qParams.set('brandIds', payload.brandIds.toString());
		}
		if (payload?.unread) {
			qParams = qParams.set('unread', payload.unread.toString());
		}
		if (payload?.privacyRightIds?.length) {
			qParams = qParams.set('privacyRightIds', payload.privacyRightIds.toString());
		}
		if (payload?.direction) {
			qParams = qParams.set('direction', SortDirectionEnum[payload.direction].toLowerCase());
		}
		if (payload?.active) {
			qParams = qParams.set('field', payload.active);
		}
		if (payload?.minCreatedAt) {
			qParams = qParams.set('minCreatedAt', payload.minCreatedAt.toString());
		}
		if (payload?.assignedGroups?.length) {
			qParams = qParams.set('assignedGroups', payload.assignedGroups.toString());
		}
		if (payload?.minDueDate) {
			qParams = qParams.set('minDueDate', payload.minDueDate.toString());
		}
		if (payload?.maxDueDate) {
			qParams = qParams.set('maxDueDate', payload.maxDueDate.toString());
		}
		if (payload?.errors?.length) {
			qParams = qParams.set('errors', payload.errors.toString());
		}
		if (payload?.requireAttention) {
			qParams = qParams.set('requireattention', payload.requireAttention.toString());
		}
		if (payload?.isNewRequest) {
			qParams = qParams.set('isNewRequest', payload.isNewRequest.toString());
		}
		if (payload?.hasNewResponse) {
			qParams = qParams.set('hasNewResponse', payload.hasNewResponse.toString());
		}
		if (payload?.stateOfResidence?.length) {
			qParams = qParams.set('stateOfResidence', payload.stateOfResidence.toString());
		}
		if (payload?.dataSubjectTypes?.length) {
			qParams = qParams.set('dataSubjectType', payload.dataSubjectTypes.toString());
		}
		if (payload?.dataSubjectTypeLabels?.length) {
			qParams = qParams.set('dataSubjectLabel', payload.dataSubjectTypeLabels.toString());
		}
		if (payload?.minVerifiedAt) {
			qParams = qParams.set('minVerifiedAt', payload.minVerifiedAt.toString());
		}
		if (payload?.closingReasons?.length) {
			qParams = qParams.set('closedReason', payload.closingReasons.toString());
		}
		if (payload?.term) {
			qParams = qParams.set('term', payload.term);
		}
		return qParams;
	}

	private getV2PaginationQueryParams(payload?: RequestsPaginationPayload): HttpParams {
		return new HttpParams()
			.set('pageNumber', payload?.pageNumber ?? this.PAGINATION_V2_DEFAULT_PAGE)
			.set('pageSize', payload?.pageSize ?? this.PAGINATION_V2_DEFAULT_SIZE);
	}
}
